import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonModal,
  IonProgressBar,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { DqText } from 'src/components/dq-text';
import { Firmware } from 'src/models/firmware';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { close, informationCircle } from 'ionicons/icons';
import './UpdateFirmwareModal.scss';
import { useSelector } from 'react-redux';
import { selectBleConnectedDevice, selectBleIsLocked, selectBleUpdateFirmware } from 'src/redux/ble/selectors';
import { useIntl } from 'react-intl';
import { getFirmwareDisplayFromFirmware } from 'src/utils/helpers/firmwareHelpers';
import { Input } from 'src/components/reusable-components/Input';
import { InputSelect } from 'src/components/reusable-components/InputSelect';

interface UpdateFirmwareProps {
  firmwares: Firmware[];
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal and reset the state.
   */
  onClose: () => void;
  /**
   * Triggers the request to close the modal and save the state object.
   */
  onSave: (firmware: Firmware | undefined) => void;
  /**
   * The current most recent firmware version.
   */
  mostRecentVersion?: Firmware;
}
export const UpdateFirmwareModal: React.FC<UpdateFirmwareProps> = ({
  firmwares,
  onClose,
  isOpen,
  onSave,
  mostRecentVersion,
}) => {
  const intl = useIntl();
  const isBleLocked = useSelector(selectBleIsLocked);
  const connectedDevice = useSelector(selectBleConnectedDevice);
  const updateFirmwareProgress = useSelector(selectBleUpdateFirmware);
  const initialState = mostRecentVersion ? mostRecentVersion : undefined;
  const [firmwareVersion, setFirmwareVersion] = useState<Firmware | undefined>(initialState);

  const progressBarValue =
    updateFirmwareProgress && updateFirmwareProgress?.writtenBytes / updateFirmwareProgress?.totalBytes;
  const jobStarted = updateFirmwareProgress && updateFirmwareProgress.writtenBytes > 0;

  useEffect(() => {
    setFirmwareVersion(initialState);
  }, [initialState, isOpen]);

  const firmwareOptions = firmwares.map(firmware => ({
    value: firmware,
    label: getFirmwareDisplayFromFirmware(intl, firmware),
  }));

  return (
    <IonModal canDismiss={!isBleLocked} isOpen={isOpen}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{intl.formatMessage(ControllerMessages.updateFirmware)}</IonTitle>
          <IonButtons slot="start">
            <IonButton slot="icon-only" onClick={() => onClose()} disabled={isBleLocked || jobStarted}>
              <IonIcon color="dark" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        {isBleLocked && jobStarted && (
          <>
            <IonProgressBar color="secondary" className="update-firmware-progress-bar" value={progressBarValue} />
            <IonItem className="update-firmware-help-text">
              <IonIcon color="secondary" slot="start" icon={informationCircle} />
              <div>
                <DqText className="update-firmware-help-text-bold">
                  {intl.formatMessage(ControllerMessages.updating)}
                </DqText>
                <DqText>{intl.formatMessage(ControllerMessages.updatingFirmwareHelpText)}</DqText>
              </div>
            </IonItem>
          </>
        )}
      </IonHeader>
      <IonContent>
        <IonRow>
          <IonCol>
            <Input
              label={intl.formatMessage(ControllerMessages.currentFirmwareVersion)}
              value={connectedDevice?.firmwareVersion}
              id="update-firmware-current-firmware-input"
              disabled
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            <InputSelect<Firmware>
              label={intl.formatMessage(ControllerMessages.selectFirmwareVersion)}
              options={firmwareOptions}
              value={firmwareVersion}
              onChange={firmware => setFirmwareVersion(firmware)}
              placeholder={intl.formatMessage(ControllerMessages.selectFirmwareVersion)}
              name="firmwareFilter"
              id="update-firmware-select-firmware-input"
            />
          </IonCol>
        </IonRow>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButton
            onClick={() => onSave?.(firmwareVersion)}
            fill="solid"
            expand="block"
            color="primary"
            disabled={!onSave || isBleLocked}
          >
            {intl.formatMessage(ControllerMessages.updateFirmware)}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};
